import { ReactNode, useCallback } from 'react';
import { Box, Text } from '@nimbus-ds/components';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import { useAgentChatPanelContext } from 'App/components/AgentChatPanelContext/hooks/AgentChatPanelContextHooks';
import ChatMarkdownLink from './components/ChatMarkdownLink';

interface ChatMarkdownParserProps {
  content: string;
  confirmed: boolean;
}

/**
 * Markdown parser for chat messages using react-markdown with Nimbus design system styling
 * Provides consistent formatting for markdown content in chat bubbles
 */
export function ChatMarkdownParser({
  confirmed,
  content,
}: Readonly<ChatMarkdownParserProps>): JSX.Element {
  const { actionBus } = useAgentChatPanelContext();

  const handleScrollToElement = useCallback(
    (target: string) => {
      actionBus?.executeScrollToElement(target);
    },
    [actionBus],
  );

  const handleNavigate = useCallback(
    (href: string) => {
      actionBus?.dispatch({
        operation: 'navigate',
        target: 'navigation',
        content: { url: href },
      });
    },
    [actionBus],
  );

  return (
    <ReactMarkdown
      components={{
        h1: ({ children }) => (
          <Box marginBottom="2">
            <Text fontWeight="bold" color="neutral-textHigh">
              {children}
            </Text>
          </Box>
        ),
        b: ({ children }) => (
          <Text fontWeight="medium" color="neutral-textHigh" as="span">
            {children as ReactNode}
          </Text>
        ),
        h2: ({ children }) => (
          <Box marginBottom="2">
            <Text
              fontSize="highlight"
              fontWeight="bold"
              color="neutral-textHigh"
            >
              {children as ReactNode}
            </Text>
          </Box>
        ),
        h3: ({ children }) => (
          <Box marginBottom="2">
            <Text fontSize="base" fontWeight="bold" color="neutral-textHigh">
              {children as ReactNode}
            </Text>
          </Box>
        ),
        p: ({ children }) => (
          <Box>
            <Text fontSize="base" color="neutral-textLow">
              {children as ReactNode}
            </Text>
          </Box>
        ),
        ul: ({ children }) => (
          <Box paddingLeft="4" marginBottom="3">
            <ul>{children as ReactNode}</ul>
          </Box>
        ),
        ol: ({ children }) => (
          <Box paddingLeft="4" marginBottom="3">
            <ol>{children as ReactNode}</ol>
          </Box>
        ),
        li: ({ children }) => (
          <Box marginBottom="1">
            <Text fontSize="base">{children as ReactNode}</Text>
          </Box>
        ),
        a: ({ href, children }) => {
          const safeHref = href ? defaultUrlTransform(href) : '';
          if (!safeHref) return <Text as="span">{children}</Text>;

          const isInternalLink = href?.startsWith('#') || href?.startsWith('/');

          return (
            <ChatMarkdownLink
              href={href}
              type={isInternalLink ? 'internal' : 'external'}
              confirmed={confirmed}
              onScrollToElement={handleScrollToElement}
              onNavigate={handleNavigate}
            >
              {children}
            </ChatMarkdownLink>
          );
        },
        strong: ({ children }) => (
          <Text fontWeight="medium" color="neutral-textHigh" as="span">
            {children as ReactNode}
          </Text>
        ),
        em: ({ children }) => <Text>{children as ReactNode}</Text>,
        blockquote: ({ children }) => (
          <Box
            paddingLeft="4"
            borderColor="neutral-surfaceHighlight"
            borderStyle="solid"
            borderWidth="1"
            marginBottom="3"
            marginTop="3"
          >
            <Text fontSize="base" color="neutral-textLow">
              {children as ReactNode}
            </Text>
          </Box>
        ),
        img: ({ src, alt }) => {
          const safeSrc = src ? defaultUrlTransform(src) : '';
          return safeSrc ? (
            <Box maxWidth="200px">
              <img src={safeSrc} alt={alt} width="100%" height="auto" />
            </Box>
          ) : null;
        },
        br: () => <Box marginBottom="1" />,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

export default ChatMarkdownParser;
